import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, Button, CircularProgress, Menu, MenuItem, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { SubNavBar } from "components/common/SubNavBar";
import { DeviceApp, HubWithStatus } from "models/hub";
import { Typography } from "new-components/typography/Typography";

import { HubActions } from "./HubActions";

interface HubSubNavBarProps {
  currentHub?: HubWithStatus | null;
  currentApp?: DeviceApp | null;
  appLoading?: boolean;
  onChangeCurrentApp?: (app: DeviceApp) => void;
  currentPage?: string;
}

export function HubSubNavBar(props: HubSubNavBarProps) {
  const { currentHub, appLoading, currentApp, onChangeCurrentApp, currentPage } = props;

  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const [appMenuAnchor, setAppMenuAnchor] = useState<null | HTMLElement>(null);

  const hasMoreThanOneApp = currentHub?.device_apps && currentHub?.device_apps.length > 1;

  const handleMenuToggle = (event?: React.MouseEvent<HTMLElement>) => {
    setAppMenuAnchor(event ? event.currentTarget : null);
    setAppMenuOpen((prev) => !prev);
  };

  return (
    <SubNavBar>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          fontWeight: 600,
          fontSize: { xs: "19px", sm: "20px" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "4px", sm: "8px" } }}>
          <Link to="/reef/hubs">
            <Typography variant="subtitle" fontWeight="semibold" textTransform="uppercase" noWrap>
              Vision Cores
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {" "}
                Overview
              </Box>
            </Typography>
          </Link>

          {currentHub?.name && (
            <>
              /
              <Tooltip title={currentHub.name} disableHoverListener={currentHub.name.length < 30} arrow>
                <Link to={`/reef/hubs/${currentHub.id}`}>
                  <Typography
                    noWrap
                    variant="subtitle"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    sx={{ maxWidth: { xs: "140px", sm: "380px", md: "none" } }}
                  >
                    {currentHub.name}
                  </Typography>
                </Link>
              </Tooltip>
              {!appLoading && !currentApp && currentPage && (
                <Typography
                  variant="subtitle"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  noWrap
                  sx={{ maxWidth: { xs: "50px", sm: "none" }, "&:hover": { textDecoration: "none" } }}
                >
                  / {currentPage}
                </Typography>
              )}
            </>
          )}

          {appLoading && onChangeCurrentApp && <CircularProgress size={20} color="inherit" className="ml-2" />}
          {/* using currentPage */}

          {!appLoading && currentApp && (
            <>
              /
              <Button
                size="small"
                disableRipple
                variant="text"
                sx={{ p: 0, color: "common.white", minWidth: 0 }}
                onClick={hasMoreThanOneApp ? handleMenuToggle : undefined}
                endIcon={hasMoreThanOneApp ? <KeyboardArrowDown /> : null}
              >
                <Typography
                  variant="subtitle"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  noWrap
                  sx={{
                    maxWidth: { xs: "50px", sm: "none" },
                    "&:hover": { textDecoration: hasMoreThanOneApp ? "underline" : "none" },
                  }}
                >
                  {currentApp.app_name}
                </Typography>
              </Button>
              <Menu
                anchorEl={appMenuAnchor}
                open={appMenuOpen}
                onClose={() => handleMenuToggle()}
                MenuListProps={{
                  role: "listbox",
                }}
              >
                {currentHub?.device_apps?.map((app) => (
                  <MenuItem
                    key={app.app_num}
                    disabled={
                      app.app_name === currentApp.app_name ||
                      currentHub?.status_detail?.app_states?.some(
                        (state) => state.app_name === app.app_name && !state.status
                      )
                    }
                    selected={app.app_name === currentApp.app_name}
                    onClick={() => {
                      if (onChangeCurrentApp) onChangeCurrentApp(app);
                      handleMenuToggle();
                    }}
                    sx={{ textTransform: "uppercase" }}
                  >
                    {app.app_name} - {app.app_description}
                  </MenuItem>
                ))}
                <MenuItem
                  key="details"
                  disabled={currentApp.app_name === "hub_device_system_status"}
                  selected={currentApp.app_name === "hub_device_system_status"}
                  onClick={() => {
                    if (onChangeCurrentApp)
                      onChangeCurrentApp({
                        app_description: "Balena device current status",
                        app_name: "Details",
                        app_num: "",
                        app_url: "",
                      });
                    handleMenuToggle();
                  }}
                  sx={{ textTransform: "uppercase" }}
                >
                  Device System Status
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
        {!appLoading && currentHub && (
          <HubActions
            hubId={currentHub.id}
            hubName={currentHub.name}
            sx={{
              color: "#FFFFFF",
              height: 24,
              width: 36,
              backgroundColor: "#529FE8",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "#529FE8",
              },
            }}
          />
        )}
      </Box>
    </SubNavBar>
  );
}
